import { Grid } from '@mui/material';
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PlanActionsOptimize from '../../../../assets/img/planActionsOptimize.svg';
import * as routing from '../../../../constants/Routing';
import { ConfirmDeleteDialog } from '../../../../dialogs/ConfirmDeleteDialog';
import { DuplicatePlanDialog } from '../../../../dialogs/DuplicatePlanDialog';
import { PlanStatusDialog } from '../../../../dialogs/PlanStatusDialog';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { PlanStatus } from '../../../../domain/plan/PlanStatus';
import { useDeleteFlatPlans } from '../../../../hooks/plan/useDeleteFlatPlans';
import { useDuplicatePlan } from '../../../../hooks/plan/useDuplicatePlan';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';
import { ActivePlanSettingsDialog } from '../dialogs/ActivePlanSettingsDialog';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';

type PlanActionsMenuProps = {
  plan: PlanFlat;
  hasDriverWithStops?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  onDeletePlan?: (deletedPlans: PlanFlat) => void;
  onDispatchPlan?: () => void;
  onUpdatePlanStatus?: () => void;
  onDuplicatePlan?: () => void;
  onUpdatePlanSettings?: () => void;
  onOptimizePlan?: () => void;
  onOpenGeoFencesDialog?: () => void;
};

export const PlanActionsMenu = ({
  plan,
  popoverProps,
  hasDriverWithStops,
  onDeletePlan,
  onDispatchPlan,
  onUpdatePlanStatus,
  onDuplicatePlan,
  onUpdatePlanSettings,
  onOptimizePlan,
  onOpenGeoFencesDialog,
}: PlanActionsMenuProps) => {
  const navigate = useNavigate();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activeProjectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const [dialog, setDialog] = useState<`delete` | `update_status` | `duplicate` | `plan_settings` | undefined>(
    undefined
  );
  const { data: userCredentials } = useRetrieveUserCredentials();

  const dispatchDisabled = useMemo(() => planDrivers?.length === 0, [planDrivers]);

  const planGeoFences = useMemo(() => activeProjectGeoFences ?? [], [activeProjectGeoFences]);

  const {
    success: duplicatePlanSuccess,
    data: createdDuplicates,
    isLoading: duplicatePlanLoading,
    run: duplicatePlan,
  } = useDuplicatePlan();
  const { success: deletePlansSuccess, run: deletePlans, isLoading: deletePlansLoading } = useDeleteFlatPlans();

  useEffect(() => {
    if (deletePlansSuccess) {
      setDialog(undefined);

      if (onDeletePlan) {
        onDeletePlan(plan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletePlansSuccess]);

  useEffect(() => {
    if (duplicatePlanSuccess) {
      setDialog(undefined);

      if (onDuplicatePlan) {
        onDuplicatePlan();
      }

      if (activeProject && (createdDuplicates ?? []).length === 1 && createdDuplicates![0] !== null) {
        setDialog(undefined);
        navigate(
          routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
            `:planId`,
            createdDuplicates![0].id
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatePlanSuccess, createdDuplicates]);

  return (
    <>
      <RoutalSelect
        id="plan-actions-menu-select"
        hideArrow={popoverProps?.hideArrow}
        style={{
          background: `transparent`,
          ...(popoverProps?.style ?? {}),
        }}
        containerStyle={popoverProps?.containerStyle}
        popoverProps={{
          maxHeight: `371px`,
          options: [
            ...[
              {
                key: `reoptimize_plan`,
                width: 280,
                height: 42,
                onClick: () => {
                  onOptimizePlan && onOptimizePlan();
                },
                content: (
                  <div style={{ display: `flex` }}>
                    <img src={PlanActionsOptimize} alt="PlanActionsOptimize" style={{ marginRight: `4px` }} />
                    <Text
                      intlId={hasDriverWithStops ? `planner.plan_actions.reoptimize` : `planner.plan_actions.optimize`}
                      style={{ cursor: `pointer` }}
                    />
                  </div>
                ),
              },
            ],
            ...[
              {
                key: `optimizer_settings`,
                width: 280,
                height: 42,
                onClick: () => {
                  onUpdatePlanSettings && onUpdatePlanSettings();
                },
                content: <Text intlId="planner.plan_actions.optimizer_settings" style={{ cursor: `pointer` }} />,
              },
            ],
            ...[
              {
                key: `dispatch`,
                width: 280,
                height: 42,
                onClick: () => {
                  if (dispatchDisabled) return;
                  onDispatchPlan && onDispatchPlan();
                },
                content: dispatchDisabled ? (
                  <Text intlId="planner.plan_actions.dispatch" style={{ color: RoutalPalette.neutral20 }} />
                ) : (
                  <Text intlId="planner.plan_actions.dispatch" style={{ cursor: `pointer` }} />
                ),
                listItemStyles: { cursor: dispatchDisabled ? `default` : `pointer` },
              },
            ],

            ...[
              null,
              {
                key: `duplicate_plan`,
                width: 280,
                height: 42,
                onClick: () => {
                  setDialog(`duplicate`);
                },
                content: <Text intlId="planner.plan_actions.duplicate_plan" style={{ cursor: `pointer` }} />,
              },
            ],
            ...[
              {
                key: `plan_settings`,
                width: 280,
                height: 42,
                onClick: () => {
                  setDialog(`plan_settings`);
                },
                content: <Text intlId="planner.plan_actions.plan_settings" style={{ cursor: `pointer` }} />,
              },
            ],
            ...[
              {
                key: `change_plan_status`,
                width: 280,
                height: 42,
                onClick: () => {
                  setDialog(`update_status`);
                },
                content: <Text intlId="planner.plan_actions.change_status" style={{ cursor: `pointer` }} />,
              },
            ],
            ...(userCredentials?.subscription.test(`planner.geofences`)
              ? [
                  null,
                  {
                    key: `geofences_settings`,
                    width: 280,
                    height: 42,
                    onClick: () => {
                      onOpenGeoFencesDialog && onOpenGeoFencesDialog();
                    },
                    content: (
                      <Grid container direction="row" alignItems="center" justifyContent="space-between">
                        <Text intlId="planner.plan_actions.geofences_settings" style={{ cursor: `pointer` }} />
                        <Text
                          style={{
                            cursor: `pointer`,
                            display: `flex`,
                            padding: `2px 6px`,
                            justifyContent: `center`,
                            alignItems: `center`,
                            gap: `10px`,
                            borderRadius: `5px`,
                            background: RoutalPalette.secondary40,
                            color: RoutalPalette.neutral00,
                          }}
                        >
                          {activeProject?.geo_fences_enabled ? planGeoFences.filter((geo) => geo.enabled).length : 0}
                        </Text>
                      </Grid>
                    ),
                  },
                ]
              : []),

            ...[
              null,
              {
                key: `delete_plan`,
                width: 280,
                height: 42,
                onClick: () => {
                  setDialog(`delete`);
                },
                content: (
                  <Text
                    intlId="planner.plan_actions.delete_plan"
                    style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                  />
                ),
              },
            ],
          ],
        }}
      >
        {popoverProps?.content}
      </RoutalSelect>

      {dialog === `duplicate` && (
        <DuplicatePlanDialog
          isLoading={duplicatePlanLoading}
          handleDuplicatePlan={(duplicates: { label: string; status: PlanStatus; execution_date: Date }[]) => {
            duplicatePlan({ sourcePlan: plan, duplicates });
          }}
          duplicateDialogOpen={dialog === `duplicate`}
          handleOpenDuplicateDialog={() => {
            setDialog(undefined);
          }}
        />
      )}

      <ConfirmDeleteDialog
        typeData="plans"
        open={dialog === `delete` || deletePlansLoading}
        onCloseDialog={() => {
          setDialog(undefined);
        }}
        data={[plan]}
        loading={deletePlansLoading}
        handleDelete={() => {
          deletePlans({ flatPlans: [plan] });
        }}
        confirmInputValue={plan?.label}
      />

      {dialog === `update_status` && (
        <PlanStatusDialog
          plans={[plan]}
          dialogOpen={dialog === `update_status`}
          onCloseDialog={() => {
            setDialog(undefined);
          }}
          onUpdatePlanStatus={() => {
            if (onUpdatePlanStatus) {
              onUpdatePlanStatus();
            }
          }}
        />
      )}

      {dialog === `plan_settings` && (
        <ActivePlanSettingsDialog
          dialogOpen={dialog === `plan_settings`}
          onCloseDialog={() => {
            setDialog(undefined);
          }}
          onUpdatePlanSettings={() => {
            if (onUpdatePlanSettings) {
              onUpdatePlanSettings();
            }
          }}
        />
      )}
    </>
  );
};
